import usePureState from '@/hooks/usePureState';
import calendarService from '@/services/calendar/calendar.service';
import { maxPlainDate, minPlainDate } from '@/services/date/date.service';
import ITimeZone from '@/types/ITimeZone';
import { Temporal } from 'temporal-polyfill';
import { IMonthCalendarEvent } from './types/IMonthCalendarEvent';

interface IProps {
	onEventClick?: (event: IMonthCalendarEvent) => void;
	events: Array<IMonthCalendarEvent>;
	locale?: string;
	timeZone: string;
	month: number;
	weekStartsOnMonday?: boolean;
	year: number;
	isLoading?: boolean;
}

interface IState {
	isSelecting: boolean;
	selectionStart: Temporal.PlainDate | null;
	selectionEnd: Temporal.PlainDate | null;
}

export function useCalendar(props: IProps) {
	const locale = props.locale || navigator.language;
	const days = calendarService.generateMonthDays({ ...props, timeZone: props.timeZone, locale });

	const { state, dispatch } = usePureState<IState>({
		isSelecting: false,
		selectionStart: null,
		selectionEnd: null,
	});

	function handleEventClick(event: IMonthCalendarEvent) {
		props.onEventClick?.(event);
	}

	const eventMap = _assignEventsToDays(
		props.events,
		days.map((d) => d.plainDate),
		props.timeZone
	);

	return {
		days,
		eventMap,
		state,
		dispatch,
		handleEventClick,
	};
}

export interface IRenderMetadata {
	starts: boolean;
	ends: boolean;
	row: number;
}

function _assignEventsToDays(
	events: Array<IMonthCalendarEvent>,
	days: Array<Temporal.PlainDate>,
	timeZone: ITimeZone
): Record<string, Array<IMonthCalendarEvent & IRenderMetadata>> {
	const eventMap: Record<string, Array<IMonthCalendarEvent & IRenderMetadata>> = {};

	// Remove events outside the month
	const filteredEvents = events.filter((event) => {
		if (event.end.epochMilliseconds < days.at(0)!.toZonedDateTime(timeZone).epochMilliseconds) {
			return false;
		}
		if (event.start.epochMilliseconds > days.at(-1)!.toZonedDateTime(timeZone).epochMilliseconds) {
			return false;
		}
		return true;
	});

	// Inicializa el objeto de salida con cada día del mes como clave y un array vacío como valor
	days.forEach((day) => {
		const dayKey = day.toString(); // Convirtiendo Temporal.PlainDate a string en formato YYYY-MM-DD
		eventMap[dayKey] = [];
	});

	for (let i = 0; i < filteredEvents.length; i++) {
		const event = filteredEvents[i];
		// Events can start before the first day or end after the last day. We ignore those days
		const startDate = maxPlainDate(event.start.toPlainDate(), days.at(0)!);
		const endDate = minPlainDate(event.end.toPlainDate(), days.at(-1)!);
		// We only care about visible days
		const diff = startDate.until(endDate).days;

		for (let j = 0; j <= diff; j++) {
			const dayWeAreEvaluating = startDate.add({ days: j });
			const dayKey = dayWeAreEvaluating.toString();
			/**
			 * Compute the row.
			 * When j === 0 it means that this is the first time we see this event in the month so we assign a row.
			 * Otherwise we assign the same row as the last day (j-1).
			 */
			const row =
				j === 0
					? _computeRowForTheFirstTime(eventMap[dayKey])
					: _getRowFromTheLastDay(event, dayWeAreEvaluating, eventMap);

			const eventWithUIData: IMonthCalendarEvent & IRenderMetadata = {
				...event,
				starts: event.start.toPlainDate().equals(dayWeAreEvaluating),
				ends: event.end.toPlainDate().equals(dayWeAreEvaluating),
				row,
			};

			eventMap[dayKey].push(eventWithUIData);
		}
	}

	return eventMap;
}

function _computeRowForTheFirstTime(dayEvents: Array<IMonthCalendarEvent & IRenderMetadata>): number {
	if (!dayEvents?.length) {
		return 1; // Devuelve 1 si no hay eventos, asumiendo que las filas empiezan en 1
	}

	// Extraer todas las filas utilizadas y ordenarlas
	const sortedRows = dayEvents.map((e) => e.row).sort((a, b) => a - b);

	// Encontrar la primera fila no utilizada
	for (let i = 0; i < sortedRows.length; i++) {
		// Dado que estamos buscando la primera fila no usada, comparamos la fila actual en el array ordenado
		// Si hay un salto entre los números, eso significa que encontramos un espacio
		if (sortedRows[i] !== i + 1) {
			return i + 1; // Retorna el primer número faltante en la secuencia
		}
	}

	// Si todas las filas desde 1 hasta el máximo están ocupadas, entonces el primer número disponible
	// será el siguiente después del último número máximo encontrado
	return sortedRows[sortedRows.length - 1] + 1;
}

function _getRowFromTheLastDay(
	event: IMonthCalendarEvent,
	dayWeAreEvaluating: Temporal.PlainDate,
	eventMap: Record<string, Array<IMonthCalendarEvent & IRenderMetadata>>
) {
	return eventMap[dayWeAreEvaluating.subtract({ days: 1 }).toString()].find((e) => e.id === event.id)?.row ?? 0;
}
